// angular
import { Params } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import {
  effect,
  inject,
  Injectable,
  signal,
  WritableSignal,
} from '@angular/core';
// services
import { StateManagementService } from '@app/+chat/shared/services/state-management.service';
import { PwjToastService } from '@app/core/shared/services/app-toast.service';
import { UserService } from '@app/shared/services/user.service';
// types & interfaces & enums
import {
  IAllAuctions,
  IAuction,
  IAuctionParticipator,
} from '../types/auctions.type';
import { BidData } from '../types/bid-data.type';
import { PriceType } from '@app/+products/shared/interfaces/product.interface';
import { CurrencyCodes } from '@app/shared/enums/currencies.enum';
import { ApiSearchTypesEnum } from '@app/core/shared/enums/api-search-types.enum';

@Injectable()
export class AuctionsService {
  // auctions list
  public auctionsList: WritableSignal<IAuction[]> = signal([]);
  public allAuctionsCount: WritableSignal<number> = signal(0);
  public isAuctionsLoading: WritableSignal<boolean> = signal(false);
  public isHomeAuctionsLoading: WritableSignal<boolean> = signal(false);

  // auction details
  public auctionDetails: WritableSignal<IAuction> = signal({});
  public isAuctionDetailsLoading: WritableSignal<boolean> = signal(false);

  // auction participators
  public auctionParticipators: WritableSignal<IAuctionParticipator[]> = signal(
    []
  );
  public isAuctionParticipatorsLoading: WritableSignal<boolean> = signal(false);

  public auctionDetailsData!: IAuction;
  public currentCurrency: keyof PriceType = CurrencyCodes.usd;

  // dependencies
  private readonly _httpClient = inject(HttpClient);
  private readonly _toast = inject(PwjToastService);
  private readonly _stateManagement = inject(StateManagementService);
  private readonly _userService = inject(UserService);

  constructor() {
    // set auction details data
    effect(() => {
      this.auctionDetailsData = this.auctionDetails();
    });
  }

  // get all Auctions observable
  public getAllAuctions$(params: Params) {
    this.isHomeAuctionsLoading.set(true);
    return this._httpClient.get<IAllAuctions>(`main/auctions`, {
      params: { search_type: ApiSearchTypesEnum.Auction, ...params },
    });
  }

  // get all Auctions
  public getAllAuctions(queryParams?: Params) {
    // set loading to true
    this.isAuctionsLoading.set(true);

    // fetch data
    return this._httpClient
      .get<IAllAuctions>(`main/auctions`, {
        params: {
          ...queryParams,
          search_type: ApiSearchTypesEnum.Auction,
        },
      })
      .subscribe({
        next: (res: IAllAuctions) => {
          const data = res.data;
          const customizedData = data?.map((auction: IAuction) => ({
            ...auction,
            start_time: auction?.start_date,
            finish_time: auction?.finish_date,
            photos: auction?.product?.images,
          }));
          // set data & data count
          this.auctionsList.set(customizedData);
          this.allAuctionsCount.set(res.count);
        },
        error: () => this.isAuctionsLoading.set(false),
        complete: () => this.isAuctionsLoading.set(false),
      });
  }

  // get auction details
  public getAuctionDetails(auctionId: string) {
    // set loading state
    this.isAuctionDetailsLoading.set(true);

    // fetch data
    return this._httpClient
      .get<IAuction>(`main/auctions/${auctionId}`)
      .subscribe({
        next: (data: IAuction) => {
          // set data
          this.auctionDetails.set(data);

          // set business id for chat
          data.business?._id &&
            this._stateManagement.businessId.set(data.business?._id);

          if (this._userService.userSignal().username && data?._id) {
            this.getAllBids(data?._id);
          }
        },
        error: () => this.isAuctionDetailsLoading.set(false),
        complete: () => this.isAuctionDetailsLoading.set(false),
      });
  }

  // submit bid for auctions
  private submitAuctionsBid(auctionId: string, data: BidData) {
    // post bid data
    return this._httpClient.post<BidData>(
      `main/auctions/${auctionId}/bids`,
      data
    );
  }

  // public method for auction bid
  public postAuctionBid(auctionId: string, data: BidData) {
    this.submitAuctionsBid(auctionId, data).subscribe({
      next: res => {
        // success message (toast)
        this._toast.success(
          'toastMessages.submitBid',
          'toastMessages.successTitle'
        );

        // update auction details data
        this.auctionDetails.set({
          ...this.auctionDetailsData,
          highest_price: res.price,
        });
      },
      error: error => {
        // auth error message (toast)
        if (error.status === 401) {
          this._toast.error(
            'toastMessages.loginMessage',
            'toastMessages.loginTitle'
          );
        } else {
          this._toast.error('toastMessages.error', 'toastMessages.errorTitle');
        }
      },
      complete: () => null,
    });
  }

  // get auction bids data
  public getAllBids(id: string) {
    // set loading state
    this.isAuctionParticipatorsLoading.set(true);

    // fetch data
    return this._httpClient
      .get<IAuctionParticipator[]>(`main/auctions/${id}/bids`)
      .subscribe({
        next: (res: IAuctionParticipator[]) => {
          this.auctionParticipators.set(res);
        },
        error: () => this.isAuctionParticipatorsLoading.set(false),
        complete: () => this.isAuctionParticipatorsLoading.set(false),
      });
  }
}
